import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import './ExpandableDescription.css';

export type ExpandableDescriptionProps = {
  text: string;
  /** When true, text is rendered as HTML (dangerouslySetInnerHTML). */
  html?: boolean;
  /** Max lines of clamp before "Read more" button is offered. Default 5. */
  maxLines?: number;
  readMoreLabel: string;
  showLessLabel: string;
  /** Optional className applied to the inner `<p>` so callers can keep
   * their existing typography rules (e.g. `.deal-description`). */
  className?: string;
};

/**
 * Renders a paragraph clamped to `maxLines` with a "Read more" toggle that
 * reveals the full text. The toggle is only mounted when the rendered text
 * actually overflows (no false promise of expansion on short content).
 *
 * Overflow detection compares `scrollHeight > clientHeight` on the underlying
 * `<p>` after layout. Recomputed on text change + window resize so a
 * responsive viewport shrink still surfaces the button.
 */
export function ExpandableDescription({
  text,
  html,
  maxLines = 5,
  readMoreLabel,
  showLessLabel,
  className,
}: ExpandableDescriptionProps) {
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const paragraphRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const node = paragraphRef.current;
      if (!node) return;
      // While clamped the scrollHeight reflects the full natural height vs
      // clientHeight the truncated render height. A 1px tolerance avoids
      // hairline diffs on Hi-DPI displays.
      setHasOverflow(node.scrollHeight - node.clientHeight > 1);
    };
    measure();
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [text, maxLines]);

  // Measure again when we collapse, because some content shifts (images
  // loading lazily, font swap) can change the natural height after first
  // mount.
  useEffect(() => {
    if (expanded || !paragraphRef.current) return;
    const node = paragraphRef.current;
    setHasOverflow(node.scrollHeight - node.clientHeight > 1);
  }, [expanded]);

  const clampedStyle =
    !expanded && maxLines > 0
      ? ({ '--clamp': String(maxLines) } as React.CSSProperties)
      : undefined;

  const paragraphClassName = [
    'expandable-description__text',
    !expanded ? 'expandable-description__text--clamped' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="expandable-description">
      {html ? (
        <div
          ref={paragraphRef as React.RefObject<HTMLDivElement>}
          className={paragraphClassName}
          style={clampedStyle}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ) : (
        <p ref={paragraphRef} className={paragraphClassName} style={clampedStyle}>
          {text}
        </p>
      )}
      {hasOverflow ? (
        <button
          type="button"
          className="expandable-description__toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? showLessLabel : readMoreLabel}
        </button>
      ) : null}
    </div>
  );
}
